import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import CountUp from "react-countup";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  User,
  Calendar,
  Clock,
  FileText,
  IndianRupee,
  AlertCircle,
  CheckCircle,
  XCircle,
  Bell,
  TrendingUp,
  Settings,
  LogOut,
  CreditCard,
  Shield,
  Briefcase,
  MapPin,
  Phone,
  Mail,
  Building2,
  Target,
  Home,
  Plus,
  RefreshCw,
  Plane,
  StickyNoteIcon,
} from "lucide-react";

import { MySalarySlips } from "../payroll";
import SimpleAttendanceCard from "./SimpleAttendanceCard";
import EmployeeLeaves from "../leaves/EmployeeLeaves";
import EmployeeSelfServiceProfile from "./EmployeeSelfServiceProfile";
import EmployeeAvatar from "../common/EmployeeAvatar";
import MyIncrement from "../employee/MyIncrement";
import EmployeeReimbursementPage from "../reimbursements/EmployeeReimbursementPage";
import MyForms from "../employee/Forms/MyForms";
import MyPFManagement from "../pf/MyPFManagement";
import EmployeeIncomeTax from "../employee/EmployeeIncomeTax";
import FestivalAdvanceSelfService from "../employee/FestivalAdvanceSelfService";
import { getAvailableModules, getModulesByCategory } from "../../config/dashboardModules";
import { buildApiUrl } from '../../utils/apiSetup';

/** Empty dashboard state — no demo/static placeholder values. */
const EMPTY_EMPLOYEE_DATA = {
  profile: {
    name: "",
    empCode: "",
    designation: "",
    department: "",
    joiningDate: "",
    phoneNumber: "",
    email: "",
    officeLocation: "",
    fatherName: "",
    motherName: "",
    sex: "",
    bankAccountNumber: "",
    bankAccountName: "",
    ifscCode: "",
    bankName: "",
    panCardNumber: "",
    aadhaarNumber: "",
    departmentCode: "",
    scale: "",
    pfCode: "",
    hbaId: "",
    photographUrl: null,
  },
  attendance: {
    todayStatus: "",
    checkInTime: "",
    checkOutTime: null,
    thisMonthPresent: 0,
    thisMonthTotal: 0,
    yearlyPresent: 0,
    yearlyTotal: 0,
  },
  leave: {
    available: { EL: 0, CL: 0, SL: 0, PL: 0, earned: 0, casual: 0, sick: 0 },
    used: { EL: 0, CL: 0, SL: 0, PL: 0 },
    pendingApplications: 0,
    balances: [],
  },
  payroll: {
    lastPayslip: "",
    grossSalary: 0,
    netSalary: 0,
    ytdGross: 0,
    ytdTds: 0,
  },
  loans: {
    activeLoans: 0,
    totalOutstanding: 0,
    monthlyEmi: 0,
    nextEmiDate: null,
  },
  notifications: [],
  recentActivities: [],
};

/** Map profile-360 API response to dashboard state shape. */
function mapProfile360ToDashboardData(data) {
  const personal = data.personal || {};
  const employment = data.employment || {};
  const payroll = data.payroll || {};
  const leaveList = data.leave || [];
  const attendance = data.attendance || {};

  const findLeave = (codes) =>
    leaveList.find((l) =>
      codes.includes(String(l.type || l.name || "").toUpperCase()),
    );

  const bal = (codes) => Number(findLeave(codes)?.current ?? 0);
  const used = (codes) => Number(findLeave(codes)?.used ?? 0);

  const el = bal(["EL", "EARNED"]);
  const cl = bal(["CL", "CASUAL"]);
  const sl = bal(["SL", "SICK"]);

  const monthPresent = Number(attendance.present ?? 0);
  const monthTotal =
    Number(attendance.total_days ?? 0) || Math.max(monthPresent, 1);

  return {
    profile: {
      name:
        personal.full_name ||
        `${personal.first_name || ""} ${personal.last_name || ""}`.trim() ||
        "",
      empCode: personal.emp_code || "",
      designation: employment.designation || "",
      department: employment.department || "",
      joiningDate: employment.join_date || "",
      phoneNumber: personal.phone || "",
      email: personal.official_email || personal.email || "",
      officeLocation:
        employment.office_location ||
        employment.work_location ||
        employment.hospital_branch ||
        "",
      fatherName: personal.father_name || "",
      motherName: personal.mother_name || "",
      sex: personal.gender || "",
      dateOfBirth: personal.date_of_birth,
      maritalStatus: personal.marital_status || "",
      bloodGroup: personal.blood_group || "",
      bankAccountNumber: payroll.bank_account_number || "",
      bankAccountName: payroll.bank_account_name || personal.full_name || "",
      bankName: payroll.bank_name || "",
      ifscCode: payroll.bank_ifsc_code || "",
      panCardNumber: personal.pan_number || "",
      aadhaarNumber: personal.aadhaar_number || "",
      departmentCode: employment.department_id || "",
      scale: employment.grade || "",
      pfCode: "",
      hbaId: "",
      photographUrl: personal.photograph_url,
    },
    attendance: {
      ...EMPTY_EMPLOYEE_DATA.attendance,
      thisMonthPresent: monthPresent,
      thisMonthTotal: monthTotal,
      presentDays: monthPresent,
      absentDays: Number(attendance.absent ?? 0),
      totalDays: monthTotal,
    },
    leave: {
      available: {
        EL: el,
        CL: cl,
        SL: sl,
        PL: bal(["PL", "PRIVILEGE"]),
        earned: el,
        casual: cl,
        sick: sl,
      },
      used: {
        EL: used(["EL", "EARNED"]),
        CL: used(["CL", "CASUAL"]),
        SL: used(["SL", "SICK"]),
        PL: used(["PL", "PRIVILEGE"]),
      },
      pendingApplications: 0,
      balances: leaveList.map((l) => ({
        type: l.name || l.type,
        available: l.current ?? 0,
        used: l.used ?? 0,
      })),
    },
    payroll: {
      grossSalary:
        Number(payroll.basic_salary || 0) +
        Number(payroll.grade_pay || 0) +
        Number(payroll.special_allowance || 0),
      netSalary: Number(payroll.basic_salary || 0),
      lastPayslip: "",
      ytdGross: 0,
      ytdTds: 0,
    },
    loans: { ...EMPTY_EMPLOYEE_DATA.loans },
    notifications: [],
    recentActivities: [],
  };
}

function mapTimelineToActivities(timeline = []) {
  return timeline.slice(0, 8).map((item, index) => ({
    id: index + 1,
    type:
      item.type === "approval"
        ? "leave"
        : item.type === "audit"
          ? "payroll"
          : item.type || "attendance",
    description: item.title || item.event || "Activity",
    date: item.date || "",
    status: item.event === "rejected" ? "pending" : "completed",
  }));
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const formatTime = (value) => (value ? String(value).slice(0, 5) : "-");
const prettyStatus = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase()) || "Unknown";

const shiftBadgeClass = (code) => {
  if (code === "NIGHT")
    return "bg-purple-100 text-purple-700 border-purple-200";
  if (code === "EMG") return "bg-red-100 text-red-700 border-red-200";
  if (code === "OFF") return "bg-gray-100 text-gray-700 border-gray-200";
  if (code === "EVE") return "bg-orange-100 text-orange-700 border-orange-200";
  return "bg-blue-100 text-blue-700 border-blue-200";
};

const AttendanceHistorySection = ({ attendanceHistory = [] }) => {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const filteredHistory = useMemo(() => {
    const search = query.trim().toLowerCase();
    return attendanceHistory.filter((item) => {
      if (filter !== "all" && item.status.toLowerCase() !== filter)
        return false;
      if (
        search &&
        ![item.date, item.status, item.shift, item.remarks]
          .join(" ")
          .toLowerCase()
          .includes(search)
      )
        return false;
      return true;
    });
  }, [filter, query]);

  const presentDays = attendanceHistory.filter((item) =>
    ["Present", "Late", "Night"].includes(item.status),
  ).length;
  const totalHours = attendanceHistory.reduce(
    (sum, item) => sum + Number(item.hours || 0),
    0,
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
          <p className="text-sm text-gray-500">History Records</p>
          <p className="text-2xl font-bold text-gray-900">
            {attendanceHistory.length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
          <p className="text-sm text-gray-500">Present/Night Days</p>
          <p className="text-2xl font-bold text-green-700">{presentDays}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
          <p className="text-sm text-gray-500">Logged Hours</p>
          <p className="text-2xl font-bold text-blue-700">
            {totalHours.toFixed(1)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              Attendance History
            </h3>
            <p className="text-sm text-gray-500">
              Previous attendance records with shift mapping and remarks.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search history"
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="present">Present</option>
              <option value="late">Late</option>
              <option value="night">Night</option>
              <option value="leave">Leave</option>
              <option value="holiday">Holiday</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                {[
                  "Date",
                  "Shift",
                  "Check In",
                  "Check Out",
                  "Hours",
                  "Status",
                  "Remarks",
                ].map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left font-semibold"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredHistory.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-blue-50/50 transition-colors"
                >
                  <td className="px-4 py-4 font-medium text-gray-900">
                    {item.date}
                  </td>
                  <td className="px-4 py-4 text-gray-700">{item.shift}</td>
                  <td className="px-4 py-4 text-gray-700">{item.checkIn}</td>
                  <td className="px-4 py-4 text-gray-700">{item.checkOut}</td>
                  <td className="px-4 py-4 font-semibold text-gray-900">
                    {Number(item.hours || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${item.status === "Present"
                        ? "bg-green-100 text-green-700"
                        : item.status === "Late"
                          ? "bg-yellow-100 text-yellow-700"
                          : item.status === "Night"
                            ? "bg-purple-100 text-purple-700"
                            : item.status === "Leave"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-700"
                        }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-gray-700">{item.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const EmployeeRosterShiftSection = ({ employeeData, rosterRows = [] }) => {
  const [view, setView] = useState("daily");
  const [selectedWeek, setSelectedWeek] = useState("current");
  const [selectedMonth, setSelectedMonth] = useState("2026-05");

  const todayIso = new Date().toISOString().slice(0, 10);
  const todayRoster =
    rosterRows.find((r) => r.date === todayIso) || rosterRows[0];
  const weeklyHours = rosterRows.reduce((sum, item) => {
    if (item.code === "OFF") return sum;
    const [start, end] = item.timing.split(" - ");
    if (!start || !end) return sum;
    const [startHour] = start.split(":").map(Number);
    const [endHour] = end.split(":").map(Number);
    return (
      sum +
      (endHour > startHour ? endHour - startHour : 24 - startHour + endHour)
    );
  }, 0);

  const monthlySummary = useMemo(() => {
    const counters = new Map();
    rosterRows.forEach((row) => {
      counters.set(row.shift, (counters.get(row.shift) || 0) + 1);
    });
    const colors = [
      "bg-blue-600",
      "bg-orange-500",
      "bg-purple-600",
      "bg-red-600",
      "bg-gray-500",
    ];
    return Array.from(counters.entries()).map(([label, count], idx) => ({
      label,
      count,
      color: colors[idx % colors.length],
    }));
  }, [rosterRows]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Calendar className="w-6 h-6 mr-2 text-blue-600" />
            Roster & Shift
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Daily, weekly, and monthly shift details for{" "}
            {employeeData.profile.name}.
          </p>
        </div>
        <div className="flex rounded-lg border border-gray-200 p-1 bg-white">
          {["daily", "weekly", "monthly"].map((item) => (
            <button
              key={item}
              onClick={() => setView(item)}
              className={`px-4 py-2 rounded-md text-sm font-semibold capitalize ${view === item ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div
          whileHover={{ y: -4 }}
          className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm"
        >
          <p className="text-sm text-gray-500">Today Shift</p>
          <p className="text-xl font-bold text-gray-900">
            {todayRoster?.shift || "-"}
          </p>
          <p className="text-sm text-blue-700">{todayRoster?.timing || "-"}</p>
        </motion.div>
        <motion.div
          whileHover={{ y: -4 }}
          className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm"
        >
          <p className="text-sm text-gray-500">Department</p>
          <p className="text-xl font-bold text-gray-900">
            {todayRoster?.ward || employeeData.profile.department}
          </p>
          <p className="text-sm text-gray-500">
            {todayRoster?.supervisor || "-"}
          </p>
        </motion.div>
        <motion.div
          whileHover={{ y: -4 }}
          className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm"
        >
          <p className="text-sm text-gray-500">Weekly Hours</p>
          <p className="text-xl font-bold text-green-700">{weeklyHours} hrs</p>
          <p className="text-sm text-gray-500">Including night duty</p>
        </motion.div>
        <motion.div
          whileHover={{ y: -4 }}
          className="bg-white rounded-lg border border-gray-100 p-5 shadow-sm"
        >
          <p className="text-sm text-gray-500">Next Change</p>
          <p className="text-xl font-bold text-orange-700">
            {rosterRows[1]?.date || "-"}
          </p>
          <p className="text-sm text-gray-500">
            {rosterRows[1]?.shift || "No upcoming shift"}
          </p>
        </motion.div>
      </div>

      {/* {view === "daily" && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <span
                className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${shiftBadgeClass(todayRoster?.code)}`}
              >
                {todayRoster?.code || "-"}
              </span>
              <h3 className="text-2xl font-bold text-gray-900 mt-3">
                {todayRoster?.shift || "-"}
              </h3>
              <p className="text-gray-600">
                {todayRoster?.day || "-"}, {todayRoster?.date || "-"} -{" "}
                {todayRoster?.timing || "-"}
              </p>
            </div>
            <button className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
              <RefreshCw className="w-4 h-4" />
              Sync Roster
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-700">Department</p>
              <p className="font-bold text-blue-950">
                {todayRoster?.ward || employeeData.profile.department}
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 hidden">
              <p className="text-sm text-green-700">Supervisor</p>
              <p className="font-bold text-green-950">
                {todayRoster?.supervisor || "-"}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-700">Handover Note</p>
              <p className="font-bold text-purple-950">
                {todayRoster?.handover || "No handover notes"}
              </p>
            </div>
          </div>
        </div>
      )} */}

      {view === "weekly" && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Weekly Roster
              </h3>
              <p className="text-sm text-gray-500">
                Editable view selector with duty status and handover notes.
              </p>
            </div>
            <select
              value={selectedWeek}
              onChange={(event) => setSelectedWeek(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="current">Current Week</option>
              <option value="next">Next Week</option>
              <option value="previous">Previous Week</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {[
                    "Date",
                    "Day",
                    "Shift",
                    "Timing",
                    "Department",
                    // "Supervisor",
                    "Status",
                  ].map((header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left font-semibold text-gray-700"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rosterRows.map((item) => (
                  <tr key={item.date} className="hover:bg-blue-50/50">
                    <td className="px-4 py-4 font-medium text-gray-900">
                      {item.date}
                    </td>
                    <td className="px-4 py-4 text-gray-700">{item.day}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${shiftBadgeClass(item.code)}`}
                      >
                        {item.shift}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-gray-700">{item.timing}</td>
                    <td className="px-4 py-4 text-gray-700">{item.ward}</td>
                    {/* <td className="px-4 py-4 text-gray-700">
                      {item.supervisor}
                    </td> */}
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-green-100 text-green-700 px-2.5 py-1 text-xs font-semibold">
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {view === "monthly" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Monthly Shift Mix
                </h3>
                <p className="text-sm text-gray-500">
                  Duty distribution for selected month.
                </p>
              </div>
              <input
                type="month"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="space-y-4">
              {monthlySummary.map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-semibold text-gray-700">
                      {item.label}
                    </span>
                    <span className="text-gray-500">
                      {item.count} duty day(s)
                    </span>
                  </div>
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(item.count / 31) * 100}%` }}
                      className={`h-full rounded-full ${item.color}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <h3 className="text-xl font-semibold text-gray-900">
              Monthly Notes
            </h3>
            <div className="mt-4 space-y-3 text-sm text-gray-500">
              {rosterRows.length === 0 ? (
                <p>No roster notes for this month.</p>
              ) : (
                rosterRows.slice(0, 3).map((row) => (
                  <div
                    key={row.id}
                    className="p-3 rounded-lg bg-gray-50 text-gray-700"
                  >
                    {row.date}: {row.shift} ({row.status})
                  </div>
                ))
              )}
            </div>
          </div> */}
        </div>
      )}
    </div>
  );
};

const EmployeeDashboard = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const activeTab = searchParams.get('tab') || 'dashboard';

  const setActiveTab = (tabId, replace = false) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!tabId || tabId === 'dashboard') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabId);
    }
    setSearchParams(nextParams, { replace });
  };
  const [employeeData, setEmployeeData] = useState(EMPTY_EMPLOYEE_DATA);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [rosterRows, setRosterRows] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Fetch employee data from API
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        if (!user?.employee_id) {
          console.log("No employee_id in user data:", user);
          setLoading(false);
          return;
        }

        const headers = {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        };

        const [profileRes, timelineRes, loansRes] = await Promise.all([
          fetch(buildApiUrl(`/api/v1/employee-master/${user.employee_id}/profile-360`), {
            headers,
          }),
          fetch(buildApiUrl(`/api/v1/employee-master/${user.employee_id}/timeline`), {
            headers,
          }),
          fetch(buildApiUrl("/api/v1/benfish-loans/my-loans"), { headers }),
        ]);

        if (profileRes.ok) {
          const data = await profileRes.json();
          const mapped = mapProfile360ToDashboardData(data);

          if (timelineRes.ok) {
            const timeline = await timelineRes.json();
            mapped.recentActivities = mapTimelineToActivities(timeline);
          }

          if (loansRes.ok) {
            const loansData = await loansRes.json();
            const loans = loansData.loans || loansData.items || loansData || [];
            const advances = loansData.advances || [];
            const activeLoans = Array.isArray(loans)
              ? loans.filter((l) =>
                ["active", "disbursed", "approved"].includes(
                  String(l.status || "").toLowerCase(),
                ),
              ).length
              : 0;
            const activeAdv = Array.isArray(advances)
              ? advances.filter((a) =>
                ["active", "disbursed", "approved"].includes(
                  String(a.status || "").toLowerCase(),
                ),
              ).length
              : 0;
            mapped.loans = {
              activeLoans: activeLoans + activeAdv,
              totalOutstanding:
                (Array.isArray(loans)
                  ? loans.reduce(
                    (s, l) =>
                      s +
                      Number(l.current_balance || l.outstanding_balance || 0),
                    0,
                  )
                  : 0) +
                (Array.isArray(advances)
                  ? advances.reduce(
                    (s, a) =>
                      s +
                      Number(a.balance_amount || a.outstanding_balance || 0),
                    0,
                  )
                  : 0),
              monthlyEmi:
                (Array.isArray(loans)
                  ? loans.reduce(
                    (s, l) =>
                      s +
                      Number(
                        l.fixed_installment ||
                        l.monthly_emi ||
                        l.next_emi_amount ||
                        0,
                      ),
                    0,
                  )
                  : 0) +
                (Array.isArray(advances)
                  ? advances.reduce(
                    (s, a) => s + Number(a.monthly_recovery || 0),
                    0,
                  )
                  : 0),
              nextEmiDate: null,
            };
          }

          setEmployeeData(mapped);
        } else {
          console.error(
            "Failed to fetch employee data:",
            profileRes.statusText,
          );
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [user, profileRefreshKey]);

  const reloadProfile = () => setProfileRefreshKey((k) => k + 1);

  useEffect(() => {
    const fetchRosterAndAttendance = async () => {
      if (!user?.employee_id) return;
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        };

        const now = new Date();
        const monday = new Date(now);
        const day = monday.getDay();
        monday.setDate(monday.getDate() + (day === 0 ? -6 : 1 - day));
        monday.setHours(0, 0, 0, 0);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        const fromDate = monday.toISOString().slice(0, 10);
        const toDate = sunday.toISOString().slice(0, 10);

        const [rosterRes, attendanceRes, todayRes, shiftsRes] =
          await Promise.all([
            fetch(buildApiUrl(`/api/v1/shifts/rosters/assignments?employee_id=${user.employee_id}&from_date=${fromDate}&to_date=${toDate}`),
              { headers },
            ),
            fetch(buildApiUrl(`/api/v1/attendance?employee_id=${user.employee_id}&page=1&size=30`),
              { headers },
            ),
            fetch(buildApiUrl("/api/v1/attendance/today"), { headers }),
            fetch(buildApiUrl("/api/v1/shifts/"), { headers }),
          ]);

        let shiftMap = new Map();
        if (shiftsRes.ok) {
          const shiftsData = await shiftsRes.json();
          shiftMap = new Map(
            (shiftsData.shifts || []).map((s) => [Number(s.id), s]),
          );
        }

        if (rosterRes.ok) {
          const rosterData = await rosterRes.json();
          const mapped = (rosterData.assignments || []).map((row) => ({
            id: row.id,
            date: row.roster_date,
            day: new Date(row.roster_date).toLocaleDateString("en-US", {
              weekday: "long",
            }),
            shift:
              shiftMap.get(Number(row.shift_id))?.name ||
              `Shift #${row.shift_id}`,
            code:
              shiftMap.get(Number(row.shift_id))?.code || `S${row.shift_id}`,
            timing: shiftMap.get(Number(row.shift_id))?.start_time
              ? `${formatTime(shiftMap.get(Number(row.shift_id))?.start_time)} - ${formatTime(shiftMap.get(Number(row.shift_id))?.end_time)}`
              : "-",
            ward: employeeData.profile.department,
            supervisor: "-",
            status: prettyStatus(row.status),
            handover: row.remarks || "No handover notes",
          }));
          setRosterRows(mapped);
        }

        if (attendanceRes.ok) {
          const attendanceData = await attendanceRes.json();
          const rows = (attendanceData.attendances || []).map((item) => ({
            id: item.id,
            date: item.attendance_date,
            checkIn: item.check_in_time ? formatTime(item.check_in_time) : "-",
            checkOut: item.check_out_time
              ? formatTime(item.check_out_time)
              : "-",
            hours: Number(item.total_hours || 0),
            status: prettyStatus(item.status),
            shift: item.shift_name || "-",
            remarks: item.remarks || "-",
          }));
          setAttendanceHistory(rows);
        }

        if (todayRes.ok) {
          const today = await todayRes.json();
          if (today) {
            setEmployeeData((prev) => ({
              ...prev,
              attendance: {
                ...prev.attendance,
                todayStatus: today.status || prev.attendance.todayStatus,
                checkInTime: today.check_in_time
                  ? formatTime(today.check_in_time)
                  : prev.attendance.checkInTime,
                checkOutTime: today.check_out_time
                  ? formatTime(today.check_out_time)
                  : prev.attendance.checkOutTime,
              },
            }));
          }
        }
      } catch (error) {
        console.error("Failed to fetch roster/attendance data", error);
      }
    };
    fetchRosterAndAttendance();
  }, [employeeData.profile.department, user?.employee_id]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest(".user-menu")) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  const InfoCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
    <motion.div
      variants={itemVariants}
      className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100 cursor-pointer"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <div className="text-2xl font-bold text-gray-900 mb-1">{value}</div>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const QuickActionButton = ({
    icon: Icon,
    label,
    onClick,
    color = "primary",
  }) => (
    <motion.button
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-6 bg-white border-2 ${color === "primary"
        ? "border-primary-200 hover:border-primary-500 hover:bg-primary-50"
        : color === "green"
          ? "border-green-200 hover:border-green-500 hover:bg-green-50"
          : color === "orange"
            ? "border-orange-200 hover:border-orange-500 hover:bg-orange-50"
            : color === "blue"
              ? "border-blue-200 hover:border-blue-500 hover:bg-blue-50"
              : color === "purple"
                ? "border-purple-200 hover:border-purple-500 hover:bg-purple-50"
                : "border-gray-200 hover:border-gray-500 hover:bg-gray-50"
        } rounded-xl transition-all duration-300 group`}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Icon
        className={`w-8 h-8 ${color === "primary"
          ? "text-primary-600"
          : color === "green"
            ? "text-green-600"
            : color === "orange"
              ? "text-orange-600"
              : color === "blue"
                ? "text-blue-600"
                : color === "purple"
                  ? "text-purple-600"
                  : "text-gray-600"
          } mb-2`}
      />
      <span className="text-sm font-medium text-gray-700 text-center">
        {label}
      </span>
    </motion.button>
  );

  const NotificationCard = ({ notification, delay }) => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay }}
      className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400"
    >
      <div className="p-1 bg-blue-100 rounded-full">
        <Bell className="w-3 h-3 text-blue-600" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-blue-900">
          {notification.title}
        </p>
        <p className="text-xs text-blue-700 mt-1">{notification.message}</p>
        <p className="text-xs text-blue-600 mt-1">{notification.time}</p>
      </div>
    </motion.div>
  );

  // StatCard component for metrics display
  const StatCard = ({ icon: Icon, title, value, subtitle, color, onClick }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <div className="text-3xl font-bold text-gray-900">{value}</div>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  // ActivityCard component for recent activities
  const ActivityCard = ({ activity, delay }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay }}
      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
    >
      <div className="flex items-center space-x-3">
        <div
          className={`p-2 rounded-full ${activity.type === "attendance"
            ? "bg-green-100"
            : activity.type === "leave"
              ? "bg-blue-100"
              : activity.type === "loan"
                ? "bg-orange-100"
                : "bg-gray-100"
            }`}
        >
          {activity.type === "attendance" && (
            <Clock className="w-4 h-4 text-green-600" />
          )}
          {activity.type === "leave" && (
            <Calendar className="w-4 h-4 text-blue-600" />
          )}
          {activity.type === "loan" && (
            <CreditCard className="w-4 h-4 text-orange-600" />
          )}
          {!["attendance", "leave", "loan"].includes(activity.type) && (
            <FileText className="w-4 h-4 text-gray-600" />
          )}
        </div>
        <div>
          <p className="font-medium text-gray-900">{activity.description}</p>
          <p className="text-sm text-gray-600">{activity.date}</p>
        </div>
      </div>
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${activity.status === "completed"
          ? "bg-green-100 text-green-800"
          : activity.status === "pending"
            ? "bg-yellow-100 text-yellow-800"
            : "bg-gray-100 text-gray-800"
          }`}
      >
        {activity.status}
      </span>
    </motion.div>
  );

  // Define tabs for employee dashboard
  const tabs = [
    { id: "dashboard", label: "Dashboard", icon: Home },
    { id: "my-profile", label: "My Profile", icon: User },
    // { id: "my-forms", label: "My Forms", icon: StickyNoteIcon },
    { id: "attendance", label: "Attendance", icon: Clock },
    // { id: "leaves", label: "Leave", icon: Plane },
    // { id: "roster-shift", label: "Roster & Shift", icon: Calendar },
    // { id: "my-increment", label: "My Increment", icon: TrendingUp },
    // { id: "my-arrears", label: "My Arrears", icon: Receipt },
    // { id: "apply-loan", label: "Apply Loan", icon: CreditCard },
    // { id: "my-loans", label: "My Loans", icon: Eye },
    // { id: "my-pf", label: "My PF", icon: Shield },
    { id: "salary-slips", label: "Salary Slips", icon: FileText },
    // { id: "income-tax", label: "Income Tax & Exemptions", icon: IndianRupee },
    //{ id: "reimbursements", label: "Employee Reimbursement", icon: FileText },
    // { id: "festival-advance", label: "Festival Advance", icon: Briefcase },
  ];

  // Render content based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case "my-forms":
        return (
          <div className="max-w-6xl mx-auto space-y-6">
            <MyForms />
          </div>
        );
      case "attendance":
        return (
          <div className="max-w-6xl mx-auto space-y-6">
            <SimpleAttendanceCard />
            <AttendanceHistorySection attendanceHistory={attendanceHistory} />
          </div>
        );
      case "leaves":
        return <EmployeeLeaves />;
      case "my-profile":
        return (
          <EmployeeSelfServiceProfile
            onProfileUpdated={reloadProfile}
            photoRefreshKey={profileRefreshKey}
          />
        );
      case "roster-shift":
        return (
          <EmployeeRosterShiftSection
            employeeData={employeeData}
            rosterRows={rosterRows}
          />
        );
      // case "apply-loan":
      //   return <LoanApplication />;
      // case "my-loans":
      //   return <MyLoans />;
      // case "my-pf":
      // case "my-vpf":
      //   return <MyPFManagement />;
      case "my-increment":
        return <MyIncrement employeeId={user?.employee_id} />;
      // case "my-arrears":
      //   return <MyArrears />;
      case "salary-slips":
        return <MySalarySlips />;
      case "income-tax":
        return <EmployeeIncomeTax />;
      case "reimbursements":
        return <EmployeeReimbursementPage />;
      // case "festival-advance":
      //   return <FestivalAdvanceSelfService />;
      default:
        return renderDashboardContent();
    }
  };

  // Extract current dashboard content into a separate function
  const renderDashboardContent = () => (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.8, delay: 0.2 }}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8"
    >
      {/* Employee Profile Section */}
      <motion.section
        initial={{ opacity: 0, scale: 0.95 }}
        animate={
          isLoaded ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }
        }
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <EmployeeAvatar
                  employeeId={user?.employee_id}
                  refreshKey={profileRefreshKey}
                  size="lg"
                  borderClass="border-2 border-white/30"
                  fallbackClass="bg-white/20 text-white"
                />
                <div>
                  <h1 className="text-2xl font-bold">
                    {user?.employee_name || employeeData.profile.name}
                  </h1>
                  <p className="text-blue-100">
                    {user?.employee_code || employeeData.profile.empCode} •{" "}
                    {employeeData.profile.designation}
                  </p>
                  {/* NEW: Display Sex and Department prominently */}
                  <p className="text-blue-200 text-sm">
                    {employeeData.profile.sex} - Department:{" "}
                    {employeeData.profile.department} - Scale:{" "}
                    {employeeData.profile.scale}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-blue-100">Department</p>
                <p className="text-lg font-semibold">
                  {employeeData.profile.department || "—"}
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab("my-profile")}
                  className="mt-2 text-sm text-white underline hover:no-underline"
                >
                  Edit profile & documents
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Enhanced Profile Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Contact Information */}
              <div className="flex items-center space-x-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Office Location</p>
                  <p className="font-medium">
                    {employeeData.profile.officeLocation}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">
                    {employeeData.profile.phoneNumber}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{employeeData.profile.email}</p>
                </div>
              </div>

              {/* NEW: Family Details */}
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Father's Name</p>
                  <p className="font-medium">
                    {employeeData.profile.fatherName}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Mother's Name</p>
                  <p className="font-medium">
                    {employeeData.profile.motherName}
                  </p>
                </div>
              </div>

              {/* NEW: Bank Details */}
              <div className="flex items-center space-x-3">
                <CreditCard className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Bank Account</p>
                  <p className="font-medium">
                    ****{employeeData.profile.bankAccountNumber.slice(-4)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {employeeData.profile.bankName}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Details Section */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Additional Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">PAN Number</p>
                  <p className="font-medium">
                    {employeeData.profile.panCardNumber}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Aadhaar Number</p>
                  <p className="font-medium">
                    {employeeData.profile.aadhaarNumber
                      ? `****${employeeData.profile.aadhaarNumber.slice(-4)}`
                      : "—"}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">IFSC Code</p>
                  <p className="font-medium">{employeeData.profile.ifscCode}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Account Holder</p>
                  <p className="font-medium">
                    {employeeData.profile.bankAccountName}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Dashboard Stats */}
      <section>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <StatCard
            icon={CheckCircle}
            title="This Month Attendance"
            value={`${employeeData.attendance?.thisMonthPresent ?? 0}/${employeeData.attendance?.thisMonthTotal ?? 0}`}
            subtitle={`${Math.round(((employeeData.attendance?.thisMonthPresent ?? 0) / Math.max(employeeData.attendance?.thisMonthTotal ?? 1, 1)) * 100)}% Present`}
            color="bg-green-600"
            onClick={() => console.log("Navigate to attendance")}
          />
          {/*
          <StatCard
            icon={Calendar}
            title="Leave Balance"
            value={`${(employeeData.leave?.available?.earned ?? 0) + (employeeData.leave?.available?.casual ?? 0) + (employeeData.leave?.available?.sick ?? 0)} Days`}
            subtitle={`EL: ${employeeData.leave?.available?.earned ?? 0} | CL: ${employeeData.leave?.available?.casual ?? 0} | SL: ${employeeData.leave?.available?.sick ?? 0}`}
            color="bg-blue-600"
            onClick={() => console.log("Navigate to leaves")}
          />
          */}
          <StatCard
            icon={IndianRupee}
            title="Last Month Salary"
            value={`₹${Math.round(employeeData.payroll.netSalary || 0).toLocaleString("en-IN")}`}
            subtitle={`Net Pay (After deductions)`}
            color="bg-purple-600"
            onClick={() => console.log("Navigate to payroll")}
          />
          {/*
          <StatCard
            icon={CreditCard}
            title="Loans & Advances"
            value={employeeData.loans.activeLoans}
            subtitle={`Monthly: ₹${Math.round(employeeData.loans.monthlyEmi || 0).toLocaleString("en-IN")}`}
            color="bg-orange-600"
            onClick={() => setActiveTab("my-loans")}
          />
          */}
        </motion.div>
      </section>

      {/* Quick Actions */}
      <section>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-6"
        >
          <div className="flex items-center mb-6">
            <Target className="w-6 h-6 text-blue-600 mr-2" />
            <h3 className="text-xl font-semibold text-gray-900">
              Quick Actions
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SimpleAttendanceCard variant="quick-action" />
            <QuickActionButton
              icon={User}
              label="My Profile"
              onClick={() => setActiveTab("my-profile")}
              color="blue"
            />
            {/*
            <QuickActionButton
              icon={Calendar}
              label="Apply Leave"
              onClick={() => setActiveTab("leaves")}
              color="blue"
            />
            */}
            {/* <QuickActionButton
              icon={Clock}
              label="Roster & Shift"
              onClick={() => setActiveTab("roster-shift")}
              color="green"
            /> */}
            <QuickActionButton
              icon={FileText}
              label="View Payslip"
              onClick={() => setActiveTab("salary-slips")}
              color="primary"
            />
            {/*
            <QuickActionButton
              icon={CreditCard}
              label="Apply Loan"
              onClick={() => setActiveTab("apply-loan")}
              color="orange"
            />
            <QuickActionButton
              icon={Eye}
              label="My Loans"
              onClick={() => setActiveTab("my-loans")}
              color="blue"
            />
            */}
            <QuickActionButton
              icon={FileText}
              label="Salary Slips"
              onClick={() => setActiveTab("salary-slips")}
              color="purple"
            />
            <QuickActionButton
              icon={Shield}
              label="My VPF"
              onClick={() => setActiveTab("my-vpf")}
              color="blue"
            />
          </div>
        </motion.div>
      </section>

      {/* Recent Activities and Notifications */}
      <div
        className={`grid grid-cols-1 ${employeeData.recentActivities?.length > 0 &&
          employeeData.notifications?.length > 0
          ? "lg:grid-cols-2"
          : ""
          } gap-8`}
      >
        {/* Recent Activities - Only show if there are activities
        {employeeData.recentActivities &&
          employeeData.recentActivities.length > 0 && (
            <motion.section
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <Clock className="w-6 h-6 text-blue-600 mr-2" />
                    <h3 className="text-xl font-semibold text-gray-900">
                      Recent Activities
                    </h3>
                  </div>
                  <span className="text-sm text-gray-500">This week</span>
                </div>
                <div className="space-y-4">
                  {employeeData.recentActivities.map((activity, index) => (
                    <ActivityCard
                      key={activity.id}
                      activity={activity}
                      delay={0.1 * index}
                    />
                  ))}
                </div>
                <button className="w-full mt-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50">
                  View All Activities
                </button>
              </div>
            </motion.section>
          )} */}

        {/* Notifications — only when backend provides items */}
        {(employeeData.notifications || []).length > 0 && (
          <motion.section
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <Bell className="w-6 h-6 text-blue-600 mr-2" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Notifications
                  </h3>
                </div>
                <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {(employeeData.notifications || []).length}
                </span>
              </div>
              <div className="space-y-4">
                {(employeeData.notifications || []).map(
                  (notification, index) => (
                    <NotificationCard
                      key={notification.id}
                      notification={notification}
                      delay={0.1 * index}
                    />
                  ),
                )}
              </div>
            </div>
          </motion.section>
        )}
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <motion.header
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Briefcase className="w-8 h-8 text-primary-900 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900 text-shadow">
                  My Dashboard
                </h1>
                <p className="text-sm text-gray-600">
                  {employeeData.profile.name} - {employeeData.profile.empCode}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors relative"
                onClick={() => setActiveTab("dashboard")}
                type="button"
              >
                {/* <Bell className="w-5 h-5" /> */}
                {(employeeData.notifications || []).length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                    {(employeeData.notifications || []).length}
                  </span>
                )}
              </motion.button>
              {/* <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Settings className="w-5 h-5" />
              </motion.button> */}

              {/* User Profile Dropdown */}
              <div className="relative user-menu">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 text-gray-700 hover:text-gray-900 transition-colors"
                >
                  <EmployeeAvatar
                    employeeId={user?.employee_id}
                    refreshKey={profileRefreshKey}
                    size="sm"
                    className="border-gray-200"
                  />
                  <span className="text-sm font-medium">
                    {user?.employee_name || "User"}
                  </span>
                </motion.button>

                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  >
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.employee_name}
                      </p>
                      {/* <p className="text-xs text-gray-500">
                        {user?.role_display_name || user?.role_name}
                      </p> */}
                    </div>
                    <motion.button
                      whileHover={{ backgroundColor: "#f3f4f6" }}
                      onClick={logout}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.header>
      <div
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            width: "100%",
            minWidth: "max-content",
          }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 border-b-2 transition-all ${activeTab === tab.id
                  ? "border-blue-600 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-600 hover:text-blue-600 hover:border-gray-300"
                  }`}
                style={{
                  flex: "1 1 0",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderContent()}
      </main>
    </div>
  );
};

export default EmployeeDashboard;
